<template>
  <div
    :class="DS_EMOJI_PICKER_CLASSES.Footer"
    data-cy="ds-emoji-picker__footer"
  >
    <div
      v-if="props.previewEmoji"
      :class="DS_EMOJI_PICKER_CLASSES.Preview"
    >
      <span>{{ props.previewEmoji.native }}</span>
      <span :class="DS_EMOJI_PICKER_CLASSES.PreviewAlias">
        :{{ props.previewEmoji.aliases[1] || props.previewEmoji.aliases[0] }}:
      </span>
    </div>

    <div :class="DS_EMOJI_PICKER_CLASSES.ToneControl">
      <DSButton
        ref="skinToneActionRef"
        :variant="DS_BUTTON_VARIANT.Ghost"
        :size="DS_SIZE.Small"
        :aria-label="SKIN_TONE_LABEL"
        :aria-expanded="skinToneOpen"
        data-cy="ds-emoji-picker__skin-tone-action"
        @click="toggleSkinTone"
      >
        {{ SKIN_TONE_LABEL }}
      </DSButton>

      <div
        v-if="skinToneOpen"
        :class="DS_EMOJI_PICKER_CLASSES.Tones"
        role="group"
        :aria-label="SKIN_TONE_LABEL"
        data-cy="ds-emoji-picker__tones"
      >
        <DSButton
          v-for="toneOption in props.toneOptions"
          :key="toneOption.tone"
          :variant="toneOption.tone === props.skinTone ? DS_BUTTON_VARIANT.Secondary : DS_BUTTON_VARIANT.Ghost"
          :size="DS_SIZE.Small"
          :aria-label="toneOption.label"
          :aria-pressed="toneOption.tone === props.skinTone"
          :data-cy="`ds-emoji-picker__tone--${toneOption.tone}`"
          @click="selectTone(toneOption.tone)"
        >
          {{ toneOption.native }}
        </DSButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, useTemplateRef } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";
import { DS_EMOJI_PICKER_CLASSES } from "@/src/design_system/button_and_controls/emoji_picker/constants/classes";
import type { EmojiPickerToneOption } from "@/src/design_system/button_and_controls/emoji_picker/types/panel";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import type { EmojiDisplayItem, EmojiSkinTone } from "@/src/shared/emoji/types";

const props = defineProps<{
  previewEmoji: EmojiDisplayItem | null;
  skinTone: EmojiSkinTone;
  toneOptions: EmojiPickerToneOption[];
}>();

const emit = defineEmits<{
  (e: "select", tone: EmojiSkinTone): void;
}>();

const skinToneOpen = ref(false);
const skinToneActionRef = useTemplateRef<InstanceType<typeof DSButton>>("skinToneActionRef");
const SKIN_TONE_LABEL = window.I18n.designSystem.emojiPicker.skinToneLabel;

const toggleSkinTone = (): void => {
  skinToneOpen.value = !skinToneOpen.value;
};

const selectTone = async (tone: EmojiSkinTone): Promise<void> => {
  emit("select", tone);
  skinToneOpen.value = false;
  await nextTick();
  (skinToneActionRef.value?.$el as HTMLButtonElement | undefined)?.focus();
};
</script>
